import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore, t } from '../stores/languageStore';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { getNicknameColor, getRoleLabel } from '../utils/roleUtils';
import EmojiPicker from '../components/EmojiPicker';


interface Message {
  id: number;
  user_id: number;
  username: string;
  nickname: string | null;
  content: string;
  image_url?: string | null;
  created_at: string;
  avatar?: string;
  role?: string;
}

const ChatRoom: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { language } = useLanguageStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [sendingIds, setSendingIds] = useState<Set<number>>(new Set());
  const [justSent, setJustSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastMessageIdRef = useRef<number>(0);

  const fetchMessages = async () => {
    try {
      const response = await api.get<any>('/chat-messages', {
        params: { page: 1, pageSize: 100 }
      });
      if (response.success) {
        setMessages(response.data || []);
        if (response.data && response.data.length > 0) {
          lastMessageIdRef.current = response.data[response.data.length - 1].id;
        }
      }
    } catch (error) {
      console.error('获取消息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewMessages = async () => {
    try {
      const response = await api.get<any>('/chat-messages', {
        params: { page: 1, pageSize: 100 }
      });
      if (response.success && response.data) {
        // 直接替换全部消息，确保同步
        setMessages(response.data || []);
        if (response.data && response.data.length > 0) {
          lastMessageIdRef.current = response.data[response.data.length - 1].id;
        }
      }
    } catch (error) {
      console.error('获取新消息失败:', error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() && !selectedImage) return;
    if (!isAuthenticated) {
      toast.error('请先登录');
      return;
    }

    const content = input.trim() || '[图片]';
    const imageData = selectedImage;
    
    const tempId = -Date.now();
    const tempMessage: Message = {
      id: tempId,
      user_id: user!.id,
      username: user!.username,
      nickname: user!.nickname || null,
      content: content,
      image_url: selectedImage || undefined,
      created_at: new Date().toISOString(),
      role: user!.role,
    };

    setMessages(prev => [...prev, tempMessage]);
    setSendingIds(prev => new Set([...prev, tempId]));
    setInput('');
    setSelectedImage(null);
    setJustSent(true);

    try {
      const response = await api.post<any>('/chat-messages', {
        content: input.trim(),
        image_data: imageData
      });

      if (response.success) {
        setMessages(prev => prev.map(msg => 
          msg.id === tempId ? { ...msg, id: response.data.id } : msg
        ));
        setSendingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(tempId);
          return newSet;
        });
        await fetchNewMessages();
      } else {
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
        setSendingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(tempId);
          return newSet;
        });
        toast.error(response.message || '发送消息失败');
      }
    } catch (error: any) {
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      setSendingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(tempId);
        return newSet;
      });
      console.error('发送消息失败:', error);
      const errorMessage = error.response?.data?.message || '发送消息失败';
      toast.error(errorMessage);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setInput(prev => prev + emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        setSelectedImage(result);
        inputRef.current?.focus();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const result = event.target?.result as string;
            if (result) {
              setSelectedImage(result);
            }
          };
          reader.readAsDataURL(file);
          e.preventDefault();
          break;
        }
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setIsAtBottom(distanceFromBottom < 50);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchNewMessages, 1500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!loading) {
      scrollToBottom();
    }
  }, [messages, loading]);

  useEffect(() => {
    if (isAtBottom || justSent) {
      scrollToBottom();
      setJustSent(false);
    }
  }, [messages, isAtBottom, justSent]);

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">{t('chat.chatRoom')}</h1>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div
          ref={containerRef}
          onScroll={handleScroll}
          className="h-[60vh] sm:h-96 overflow-y-auto p-4 border-b"
        >
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              {t('chat.noMessages')}
            </div>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`flex mb-4 ${msg.user_id === user?.id ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[70%] ${msg.user_id === user?.id ? 'text-right' : 'text-left'}`}>
                  <div className="flex items-center mb-1">
                    {msg.user_id !== user?.id && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                        <span className="text-sm font-medium">{(msg.nickname || msg.username)?.[0]}</span>
                      </div>
                    )}
                    <span className={`text-xs font-medium ${getNicknameColor(msg.role || '普通用户')}`}>
                      {getRoleLabel(msg.role || '普通用户')}{msg.nickname || msg.username}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">
                      {new Date(msg.created_at).toLocaleTimeString('zh-CN')}
                    </span>
                  </div>
                  <div 
                    className={`inline-block p-3 rounded-lg ${msg.user_id === user?.id ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-800'}`}
                  >
                    {msg.image_url ? (
                      <img
                        src={msg.image_url}
                        alt={t('chat.imageMessage')}
                        className="max-w-xs rounded-lg"
                        onClick={() => window.open(msg.image_url, '_blank')}
                        style={{ cursor: 'pointer' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%239ca3af%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Crect x=%223%22 y=%223%22 width=%2218%22 height=%2218%22 rx=%222%22 ry=%222%22%3E%3C/rect%3E%3Ccircle cx=%228.5%22 cy=%228.5%22 r=%221.5%22%3E%3C/circle%3E%3Cpolyline points=%2221 15 16 10 5 21%22%3E%3C/polyline%3E%3C/svg%3E`;
                          target.style.opacity = '0.5';
                        }}
                      />
                    ) : (
                      <span className="whitespace-pre-wrap">{msg.content}</span>
                    )}
                    {sendingIds.has(msg.id) && (
                      <span className="ml-2 inline-block animate-pulse text-xs opacity-60">{t('chat.sending')}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4">
          {selectedImage && (
            <div className="mb-3 relative inline-block">
              <img
                src={selectedImage}
                alt={t('chat.preview')}
                className="max-h-32 rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                ×
              </button>
            </div>
          )}
          <div className="flex items-center">
            <div className="relative">
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-l-md transition-colors"
              >
                <span className="text-xl">😄</span>
              </button>
              {showEmojiPicker && (
                <div className="absolute bottom-full left-0 mb-2 z-50">
                  <EmojiPicker onSelect={handleEmojiSelect} />
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <span className="text-xl">📷</span>
            </button>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              onPaste={handlePaste}
              className="flex-1 px-4 py-2 border-y border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('chat.placeholder')}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() && !selectedImage}
              className="px-6 py-2 bg-pink-500 text-white rounded-r-md hover:bg-pink-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <span className="text-xl">➤</span>
            </button>
          </div>
          {!isAuthenticated && (
            <p className="text-xs text-gray-500 mt-2">{t('chat.pleaseLogin')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatRoom;